import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Clock, Copy, FileText } from 'react-feather';
import 'dayjs/locale/sv';

import dayjs from 'dayjs';

import { Button, Link, Spinner } from '@/components';
import { Trans, useTranslation } from 'react-i18next';
import { useCollaboration, useDrafts, usePublications, useScreenshots } from '@/hooks/influencer';

import { TranslatedLink } from '@/utils/translatedComponents';
import { SelectInput } from '@/components/select-input/SelectInput';
import { Drawer } from '@/components/drawer/Drawer';
import { ExpandableBox } from '@/components/expandable-box';
import { getFileIcon, PlatformIcon } from '@/utils/platformIcon';
import { message } from 'antd/lib';
import Footer from '@/components/footer/Footer';
import styles from './Collaboration.module.css';
import { Draft } from './components/collaboration-item/Draft';
import { Publication } from './components/collaboration-item/Publication';
import { Report } from './components/collaboration-item/Report';
import DraftSubmitted from '../../../public/assets/icons/drafts-submitted.svg';
import DraftEmpty from '../../../public/assets/icons/drafts-empty.svg';
import SubmitModal from './components/modals/report-modal/SubmitModal';
import DraftSuccessModal from './components/modals/draft-success-modal/DraftSuccessModal';
import ChangeDatesModal from './components/modals/change-dates-modal/ChangeDatesModal';

const DRAFT_TABS = ['todo', 'waitingForReview', 'inReview', 'approved', 'all'] as const;
type TabKey = (typeof DRAFT_TABS)[number];
export interface CollaborationProps {
  collaborationId: string;
  beatId?: string;
  tab?: string;
}

export function Collaboration({
  collaborationId,
  beatId: propBeatId,
  tab: propTab
}: CollaborationProps) {
  const [activeBeat, setActiveBeat] = useState<null | string>(null);
  const [activeTab, setActiveTab] = useState('DRAFTS');
  const [activeDraftTab, setActiveDraftTab] = useState<TabKey>('todo');
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [collabStatus, setCollabStatus] = useState('');
  const [submitDraftPopup, setSubmitDraftPopup] = useState(false);
  const [changePublicationDate, setChangePublicationDate] = useState(false);

  const { t } = useTranslation();

  const STEPS = [
    {
      key: 'DRAFTS',
      title: t('collaborations.tabs.drafts')
    },
    {
      key: 'PUBLICATIONS',
      title: t('collaborations.tabs.publications')
    },
    {
      key: 'SCREENSHOTS1',
      title: t('collaborations.tabs.firstResults')
    },
    {
      key: 'SCREENSHOTS2',
      title: t('collaborations.tabs.finalResults')
    }
  ];

  const beatId = propBeatId;

  const { data: collaborationData, isLoading: isLoadingCollaboration } = useCollaboration({
    id: collaborationId,
    onSuccess: (data) => {
      if ('beats' in data) {
        const id = data.beats.some((beat) => beat.beat_id === beatId)
          ? beatId
          : data.beats[0].beat_id;
        setActiveBeat((id as string) || null);

        const selectedBeat =
          data.beats_steps.find((beat: Api.Influencer.BeatSteps) => beat.beat_id === id) ||
          data.beats_steps[0];
        const activeStep =
          selectedBeat.steps.find((step: Api.Influencer.BeatStep) => !step.is_done)?.step ??
          selectedBeat.steps[selectedBeat.steps.length - 1].step;

        setActiveTab(propTab || activeStep);

        if (!propTab) {
          // Update URL with the active step if no tab prop is provided
          const urlParams = new URLSearchParams(window.location.search);
          urlParams.set('tab', activeStep);
          // Use router.push instead of window.history.replaceState to avoid hard navigation conflicts
          if (typeof window !== 'undefined' && window.history?.replaceState) {
            window.history.replaceState(null, '', `?${urlParams.toString()}`);
          }
        }
      }
    }
  });

  const {
    drafts,
    isLoading: isLoadingDrafts,
    refetch: refetchDrafts
  } = useDrafts({
    id: collaborationId,
    beat_id: activeBeat as string
  });

  const {
    publications,
    isLoading: isLoadingPublications,
    refetch: refetchPublications
  } = usePublications({
    id: collaborationId,
    beat_id: activeBeat as string
  });
  const {
    screenshots,
    isLoading: isLoadingScreenshots,
    refetch: refetchScreenshots
  } = useScreenshots({
    id: collaborationId,
    beat_id: activeBeat as string,
    step: activeTab
  });

  const useFilteredSteps = (
    collabData: Api.Influencer.Collaboration | undefined,
    currentBeat: string,
    steps: typeof STEPS
  ) => {
    const beat: Api.Influencer.BeatSteps = useMemo(() => {
      return collabData?.beats_steps?.find((step) => step.beat_id === activeBeat);
    }, [collabData, currentBeat]);

    const filteredStepsList = useMemo(() => {
      if (!beat) return [];
      return steps
        .map((step) => {
          const matchedStep = beat.steps.find((s) => s.step === step.key);
          return matchedStep ? { ...step, is_done: matchedStep.is_done as boolean } : null;
        })
        .filter((s): s is NonNullable<typeof s> => !!s);
    }, [beat, steps]);

    return { beat, filteredStepsList };
  };

  const { filteredStepsList } = useFilteredSteps(collaborationData, activeBeat as string, STEPS);

  function fetchTabData() {
    if (activeTab === 'DRAFTS') {
      refetchDrafts();
    }
    if (activeTab === 'PUBLICATIONS') {
      refetchPublications();
    }
    if (activeTab === 'SCREENSHOTS1' || activeTab === 'SCREENSHOTS2') {
      refetchScreenshots();
    }
  }

  const filteredDrafts = useMemo(() => {
    return {
      todo: drafts.filter(
        (item: Api.Influencer.Draft) =>
          item.status === 'NOT_SUBMITTED' || item.status === 'REJECTED'
      ),
      inReview: drafts.filter((item: Api.Influencer.Draft) => item.status === 'IN_REVIEW'),
      waitingForReview: drafts.filter((item: Api.Influencer.Draft) => item.status === 'SUBMITTED'),
      approved: drafts.filter((item: Api.Influencer.Draft) => item.status === 'APPROVED'),
      all: drafts
    };
  }, [drafts]);

  useEffect(() => {
    if (hasInitialLoadCompleted || !drafts || drafts.length === 0) return;

    const firstNonEmptyTab = filteredDrafts.todo.length
      ? 'todo'
      : filteredDrafts.inReview.length
        ? 'inReview'
        : filteredDrafts.waitingForReview.length
          ? 'waitingForReview'
          : filteredDrafts.approved.length
            ? 'approved'
            : 'all';

    setActiveDraftTab(firstNonEmptyTab);
    setHasInitialLoadCompleted(true);
  }, [drafts, filteredDrafts, hasInitialLoadCompleted]);

  useEffect(() => {
    if (!activeBeat) return;
    fetchTabData();
  }, [activeBeat, activeTab]);

  function handleBeatChange(val: string) {
    setActiveBeat(val);
    setHasInitialLoadCompleted(false);

    const selectedBeat =
      collaborationData.beats_steps.find(
        (beat: Api.Influencer.BeatSteps) => beat.beat_id === val
      ) || collaborationData.beats_steps[0];
    const activeStep =
      selectedBeat.steps.find((step: Api.Influencer.BeatStep) => !step.is_done)?.step ??
      selectedBeat.steps[selectedBeat.steps.length - 1].step;

    setActiveTab(activeStep);

    if (activeStep !== propTab) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('tab', activeStep);
      // Use router.push instead of window.history.replaceState to avoid hard navigation conflicts
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        window.history.replaceState(null, '', `?${urlParams.toString()}`);
      }
    }
  }
  function handleStepChange(step: string) {
    if (activeTab !== step) {
      setActiveTab(step);
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('tab', step);
      // Use router.push instead of window.history.replaceState to avoid hard navigation conflicts
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        window.history.replaceState(null, '', `?${urlParams.toString()}`);
      }
    }
  }

  if (collaborationData && 'code' in collaborationData) {
    return null;
  }

  const handleCopy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(link);
      message.success(t('alert.COPIED'));
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      console.error('Copy error', error);
    }
  };

  const activeBeatObject = collaborationData?.beats.find((beat) => beat.beat_id === activeBeat);

  const filteredTrackers = collaborationData?.trackers?.filter(
    (link) => activeBeatObject?.beat_name && link.tracker_note?.includes(activeBeatObject.beat_name)
  );

  const EmptyState = (
    <div className={styles.noData}>
      {activeTab === 'DRAFTS' && activeDraftTab === 'todo' && !filteredDrafts.todo.length ? (
        <>
          <DraftSubmitted />
          <span>{t('collaborations.empty.submit')}</span>
        </>
      ) : (
        <>
          <DraftEmpty />
          <span>{t(`collaborations.empty.${activeTab}`)}</span>
        </>
      )}
    </div>
  );

  return isLoadingCollaboration ? (
    <div className={styles.AuthWrap}>
      <div className={styles.AuthSpinner}>
        <Spinner />
      </div>
    </div>
  ) : !collaborationData || !collaborationData.campaign ? (
    <div className={styles.AuthWrap}>
      <div className={styles.AuthSpinner}>
        <Spinner />
      </div>
    </div>
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.linkBackWrap}>
        <TranslatedLink href="/connect/collaborations" style={{ alignSelf: 'flex-start' }}>
          <div className={styles.linkBack}>
            <ChevronLeft />
            <span>{t('collaborations.allCampaigns')}</span>
          </div>
        </TranslatedLink>
        <Button onClick={() => setShowBrief(true)}>
          <FileText size={16} />
          <span>{t('inviteView.firstCollabModal.button')}</span>
        </Button>
      </div>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>{collaborationData.campaign.brand_name}</div>
          <div className={styles.brand}>
            {collaborationData.campaign.brand_logo_url && (
              <img
                className={styles.brandLogo}
                src={collaborationData.campaign.brand_logo_url}
                alt=""
              />
            )}
            <div className={styles.brandName}>{collaborationData.campaign.campaign_name}</div>
          </div>
        </div>
        {activeBeat && (
          <div className={styles.headerBeat}>
            <span>{t('inviteView.beat')}:</span>
            <SelectInput
              value={activeBeat}
              options={collaborationData.beats.map((b) => ({
                label: b.beat_name,
                value: b.beat_id
              }))}
              onChange={handleBeatChange}
              noSpacing
            />
          </div>
        )}
      </div>
      <div className={styles.stepsWrap}>
        <div className={styles.steps}>
          {filteredStepsList.map((step, i) => (
            <div
              key={step.key}
              className={`${styles.tabItem} ${activeTab === step.key ? styles.active : ''}`}
              onClick={() => handleStepChange(step.key)}
            >
              {step.is_done ? (
                <div className={`${styles.tabStep} ${styles.checked}`}>
                  <Check size="100%" color="white" />
                </div>
              ) : (
                <div className={`${styles.tabStep} ${activeTab === step.key ? styles.active : ''}`}>
                  {i + 1}
                </div>
              )}
              <div className={styles.tabTitle}>{step.title}</div>
              {i + 1 < filteredStepsList.length && <ChevronRight size={16} color="#474E5480" />}
            </div>
          ))}
        </div>
        <Button onClick={() => setShowBrief(true)}>
          <FileText size={16} />
          <span>{t('inviteView.firstCollabModal.button')}</span>
        </Button>
      </div>

      <div className={styles.body}>
        {isLoadingDrafts || isLoadingPublications || isLoadingScreenshots ? (
          <div className={styles.AuthWrap}>
            <div className={styles.AuthSpinner}>
              <Spinner />
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'DRAFTS' && drafts && (
              <>
                <div className={styles.contentHeader}>
                  <div className={styles.headerTabs}>
                    <span>{t('campaignPageCollaborations.show')}:</span>
                    {DRAFT_TABS.map((tab) => (
                      <span
                        key={tab}
                        className={`${styles.tab} ${activeDraftTab === tab ? styles.active : ''}`}
                        onClick={() => setActiveDraftTab(tab)}
                      >
                        {t(`collaborationsStatistics.${tab}`)}
                        <span> ({filteredDrafts[tab].length})</span>
                      </span>
                    ))}
                  </div>
                  <div className={styles.headerTabsSm}>
                    <SelectInput
                      value={activeDraftTab}
                      options={DRAFT_TABS.map((tab) => ({
                        label: `${t(`collaborationsStatistics.${tab}`)} (${filteredDrafts[tab].length})`,
                        value: tab
                      }))}
                      noSpacing
                      onChange={(val) => setActiveDraftTab(val)}
                    />
                  </div>
                  {!!drafts.length && drafts[0].date_draft && (
                    <div className={styles.headerDeadline}>
                      {t('collaboration.draftsDeadline')}:
                      <span> {dayjs(drafts[0].date_draft).format('YYYY-MM-DD')}</span>
                    </div>
                  )}
                </div>
                {filteredDrafts[activeDraftTab] && filteredDrafts[activeDraftTab].length
                  ? filteredDrafts[activeDraftTab].map((item: Api.Influencer.Draft) => (
                      <Draft
                        key={item.product_id}
                        draft={item}
                        setStatus={setCollabStatus}
                        setSuccess={() => setSubmitDraftPopup(true)}
                      />
                    ))
                  : EmptyState}
              </>
            )}
            {activeTab === 'PUBLICATIONS' &&
              (publications && publications.length ? (
                <>
                  <p style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button theme="cta" onClick={() => setChangePublicationDate(true)}>
                      {t('collaborations.publish.changeDate')}
                    </Button>
                  </p>
                  {publications.map((item: Api.Influencer.Publication) => (
                    <Publication
                      key={item.publication_id}
                      publication={item}
                      setStatus={setCollabStatus}
                    />
                  ))}
                </>
              ) : (
                EmptyState
              ))}

            {(activeTab === 'SCREENSHOTS1' || activeTab === 'SCREENSHOTS2') &&
              (screenshots && screenshots.length ? (
                <>
                  {screenshots.map((item: Api.Influencer.Screenshot) => (
                    <Report
                      key={item.request_id}
                      report={item}
                      setStatus={setCollabStatus}
                      step={activeTab}
                    />
                  ))}
                  {dayjs(screenshots[0].date_report).isAfter(dayjs()) && (
                    <div className={styles.preventLayer}>
                      <Clock />
                      <div className={styles.preventTitle}>
                        <Trans
                          i18nKey="collaborations.report.title"
                          values={{
                            count: dayjs(screenshots[0].date_report).diff(dayjs(), 'day') + 1
                          }}
                          components={{
                            1: <span />
                          }}
                        />
                      </div>
                      <div className={styles.preventDescription}>
                        <Trans
                          i18nKey="collaborations.report.description"
                          values={{
                            date: dayjs(screenshots[0].date_report).format('DD.MM.YYYY')
                          }}
                          components={{
                            1: <span />
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                EmptyState
              ))}
          </>
        )}

        {!!filteredTrackers.length && ['DRAFTS', 'PUBLICATIONS'].includes(activeTab) && (
          <>
            <div className={styles.links}>{t('inviteView.trackingUrl')}</div>
            <div className={styles.briefLinks}>
              {filteredTrackers.map((tracker) => (
                <div key={tracker.tracker_url} className={styles.briefLinkItem}>
                  <a
                    href={tracker.tracker_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.briefLink}
                  >
                    {tracker.tracker_url}
                  </a>
                  <div className={styles.briefLinkIcon}>
                    <button
                      type="button"
                      onClick={() => handleCopy(tracker.tracker_url)}
                      className={styles.briefLinkBtn}
                    >
                      <Copy
                        size={20}
                        color={copiedLink === tracker.tracker_url ? 'green' : '#474e54'}
                      />
                    </button>
                    {tracker.tracker_note && (
                      <span style={{ fontSize: '14px', color: '#555' }}>
                        {tracker.tracker_note}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div>
        <Footer />
      </div>
      {collabStatus && (
        <SubmitModal
          type={collabStatus}
          companyName={collaborationData.campaign.campaign_name}
          onCancel={() => setCollabStatus('')}
        />
      )}
      {submitDraftPopup && <DraftSuccessModal onCancel={() => setSubmitDraftPopup(false)} />}
      {collaborationData && changePublicationDate && activeBeatObject && (
        <ChangeDatesModal
          collabId={collaborationData.collaboration_id}
          beat={activeBeatObject}
          onUpdate={refetchPublications}
          onCancel={() => setChangePublicationDate(false)}
        />
      )}
      {showBrief && (
        <Drawer
          theme="gray"
          title={t('inviteView.campaignBrief')}
          onClose={() => setShowBrief(false)}
        >
          <div>
            {(collaborationData.campaign.brief.description.trim() ||
              !!collaborationData.campaign.brief.files.length) && (
              <ExpandableBox
                title={<div className={styles.briefLabel}>{t('inviteView.campaignBrief')}</div>}
              >
                {!!collaborationData.campaign.brief.files.length && (
                  <div className={styles.files}>
                    {collaborationData.campaign.brief.files.map((file) => (
                      <Link key={file.url} href={file.url} target="_blank">
                        <div className={styles.file}>
                          {getFileIcon(file.filename)}
                          {file.filename}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                {collaborationData.campaign.brief.description.trim() && (
                  <span
                    className={styles.briefInfo}
                    dangerouslySetInnerHTML={{
                      __html: collaborationData.campaign.brief.description
                    }}
                  />
                )}
              </ExpandableBox>
            )}

            {collaborationData.beats.map((beatItem, i) => {
              const products = Object.entries(beatItem.products || {}).map(([key, value]) => ({
                key,
                value
              }));
              return (
                <ExpandableBox
                  key={beatItem.beat_id}
                  title={
                    <div className={styles.briefLabel}>
                      <span>
                        {t('inviteView.beat')} {i + 1}
                      </span>
                      <span className={styles.beatLabel}>{beatItem.beat_name}</span>
                    </div>
                  }
                >
                  <div className={styles.beatInfo}>
                    <div className={styles.beatInfoRow}>
                      <div className={styles.beatInfoLabel}>{t('inviteView.task')}:</div>
                      <div className={styles.beatInfoContentFlex}>
                        {PlatformIcon(beatItem.platform)}
                        {products.map(({ key, value }) => (
                          <div key={key} className={styles.platformItem}>
                            <span>{value}</span> {key}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className={styles.beatInfoRow}>
                      <div className={styles.beatInfoLabel}>{t('inviteView.draftsDate')}:</div>
                      <div className={styles.beatInfoContent}>
                        {dayjs(beatItem.date_draft).format('YYYY-MM-DD')}
                      </div>
                    </div>
                    {beatItem.dates_publish && (
                      <div className={styles.beatInfoRow}>
                        <div className={styles.beatInfoLabel}>
                          {t('inviteView.publicationDate')}:
                        </div>
                        <div className={styles.beatInfoContent}>
                          {beatItem.dates_publish.map((date, index) => (
                            <span key={date}>
                              {dayjs(date).format('DD.MM.YYYY')}
                              {index < beatItem.dates_publish.length - 1 && (
                                <span className={styles.beatInfoContentSpan}>
                                  {` ${t('inviteView.or')} `}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {!!beatItem.brief.files.length && (
                    <div className={styles.files}>
                      {beatItem.brief.files.map((file) => (
                        <Link key={file.url} href={file.url} target="_blank">
                          <div className={styles.file}>
                            {getFileIcon(file.filename)}
                            {file.filename}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {beatItem.brief.description.trim() && (
                    <span
                      className={styles.briefInfo}
                      dangerouslySetInnerHTML={{
                        __html: beatItem.brief.description
                      }}
                    />
                  )}
                </ExpandableBox>
              );
            })}

            {!!collaborationData.trackers.length && (
              <>
                <div className={styles.links}>{t('inviteView.trackingUrl')}</div>
                <div className={styles.briefLinks}>
                  {collaborationData.trackers.map((tracker) => (
                    <div key={tracker.tracker_url} className={styles.briefLinkItem}>
                      <a
                        href={tracker.tracker_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.briefLink}
                      >
                        {tracker.tracker_url}
                      </a>
                      <div className={styles.briefLinkIcon}>
                        <button
                          type="button"
                          onClick={() => handleCopy(tracker.tracker_url)}
                          className={styles.briefLinkBtn}
                        >
                          <Copy
                            size={20}
                            color={copiedLink === tracker.tracker_url ? 'green' : '#474e54'}
                          />
                        </button>
                        {tracker.tracker_note && (
                          <span style={{ fontSize: '14px', color: '#555' }}>
                            {tracker.tracker_note}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Drawer>
      )}
    </div>
  );
}
